import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getRepFirm, updateRepFirm, deleteRepFirm } from '../api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { ArrowLeft, Edit, Trash2 } from 'lucide-react';
import EmptyValue from '../components/EmptyValue';
import useDetailBreadcrumbs from '../hooks/useDetailBreadcrumbs';

function RepFirmDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, canDo } = useAuth();
  const [repFirm, setRepFirm] = useState(null);
  useDetailBreadcrumbs(repFirm ? [{ label: 'Rep Firms', path: '/rep-firms' }, { label: repFirm.name }] : null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const response = await getRepFirm(id);
      setRepFirm(response.data);
      setFormData(response.data);
    } catch (err) {
      setError('Failed to load rep firm');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateRepFirm(id, formData);
      setShowEditModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteRepFirm(id);
      navigate('/rep-firms');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;
  if (!repFirm) return <div className="alert alert-error">Rep firm not found</div>;

  return (
    <div className="rep-firm-detail">
      <div className="page-header">
        <div className="flex items-center gap-4">
          <button className="btn btn-icon" onClick={() => navigate('/rep-firms')} aria-label="Go back"><ArrowLeft size={20} /></button>
          <h1 className="page-title">{repFirm.name}</h1>
        </div>
        <div className="page-actions">
          {canDo('rep_firms', 'edit') && (
            <button className="btn btn-primary" onClick={() => setShowEditModal(true)}><Edit size={18} /> Edit</button>
          )}
          {canDo('rep_firms', 'delete') && (
            <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)} aria-label="Delete rep firm"><Trash2 size={18} /></button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert" aria-live="assertive">{error}</div>}

      <div className="grid grid-cols-2">
        <div className="card">
          <div className="card-header"><h3 className="card-title">Details</h3></div>
          <div className="card-body">
            <div className="detail-row"><span className="detail-label">Website</span><span className="detail-value">{repFirm.website ? <a href={repFirm.website} target="_blank" rel="noreferrer">{repFirm.website}</a> : <EmptyValue label="Add website" onClick={canDo('rep_firms', 'edit') ? () => setShowEditModal(true) : undefined} />}</span></div>
            <div className="detail-row"><span className="detail-label">Phone</span><span className="detail-value">{repFirm.phone || <EmptyValue label="Add phone" onClick={canDo('rep_firms', 'edit') ? () => setShowEditModal(true) : undefined} />}</span></div>
            <div className="detail-row"><span className="detail-label">Address</span><span className="detail-value">{repFirm.address || <EmptyValue label="Not set" onClick={canDo('rep_firms', 'edit') ? () => setShowEditModal(true) : undefined} />}</span></div>
            <div className="detail-row"><span className="detail-label">Country</span><span className="detail-value">{repFirm.country || <EmptyValue label="Not set" onClick={canDo('rep_firms', 'edit') ? () => setShowEditModal(true) : undefined} />}</span></div>
            <div className="detail-row"><span className="detail-label">State</span><span className="detail-value">{repFirm.state || <EmptyValue label="Not set" onClick={canDo('rep_firms', 'edit') ? () => setShowEditModal(true) : undefined} />}</span></div>
            {repFirm.notes && <div className="detail-row"><span className="detail-label">Notes</span><span className="detail-value">{repFirm.notes}</span></div>}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3 className="card-title">Linked Accounts ({repFirm.accounts?.length || 0})</h3></div>
          <div className="card-body">
            {repFirm.accounts?.length > 0 ? (
              <ul>{repFirm.accounts.map(a => <li key={a.id}><Link to={`/accounts/${a.id}`}>{a.name}</Link></li>)}</ul>
            ) : <p className="text-secondary">No linked accounts</p>}
          </div>
        </div>
      </div>

      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Rep Firm">
        <form onSubmit={handleUpdate}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input type="text" className="form-input" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">Website</label>
                <input type="url" className="form-input" value={formData.website || ''} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input type="tel" className="form-input" value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Address</label>
              <input type="text" className="form-input" value={formData.address || ''} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
            </div>
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">Country</label>
                <input type="text" className="form-input" value={formData.country || ''} onChange={(e) => setFormData({ ...formData, country: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">State</label>
                <input type="text" className="form-input" value={formData.state || ''} onChange={(e) => setFormData({ ...formData, state: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} onConfirm={handleDelete} title="Delete Rep Firm" message={`Delete ${repFirm.name}? This cannot be undone.`} confirmText="Delete" />
    </div>
  );
}

export default RepFirmDetail;
